import React, { useState, useMemo, useRef, useEffect } from "react";
import { Table, SelectPicker, Input, Pagination, Button, Whisper, Tooltip } from "rsuite";
import { CSVLink } from "react-csv";
const { Column, HeaderCell, Cell } = Table;

export interface ColumnConfig {
  key: string;
  title: string;
  width: number;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  fixed?: "left" | "right";
  cellType?: "text" | "dropdown" | "input";
  options?: { label: string; value: string | number }[];
}

interface TableComponentProps {
  data: any[];
  columns: ColumnConfig[];
  totalRows: number;
  rowsPerPage: number;
  currentPage: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onDataChange: (updatedData: any[], modifiedRows: any[]) => void;
  onDropdownAction: (rowId: number, dropdownValues: any) => void;
  onSave?: () => void;
  onExport?: () => void;
  onUpdate?: () => void;
  tableHeight?: number;
  onRowSelect: (selectedKeys: any[], rowData: any) => void;
  exportData: any[];
  exportHeaders: { label: string; key: string }[];
}

const TableComponent: React.FC<TableComponentProps> = ({
  data,
  columns,
  totalRows,
  rowsPerPage,
  currentPage,
  onPageChange,
  loading,
  onDataChange,
  onDropdownAction,
  onSave,
  onExport,
  onUpdate,
  tableHeight,
  onRowSelect,
  exportData,
  exportHeaders,
}) => {
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortType, setSortType] = useState<"asc" | "desc" | undefined>();
  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [limit, setLimit] = useState(rowsPerPage);
  const [activePage, setActivePage] = useState(currentPage);
  const [modifiedRows, setModifiedRows] = useState<Set<number>>(new Set()); // Track modified rows
  const csvLinkRef = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const [isExportTriggered, setIsExportTriggered] = useState(false);
  const handleSave = () => {
    if (onSave) {
      onSave();
      setModifiedRows(new Set());
    }
    setIsUpdateEnabled(true);
  };

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    }
    setIsUpdateEnabled(false);
  };
  // Sort and paginate data
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortType) return data;

    // Apply sorting
    return [...data].sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];

      // Handle case-insensitive string sorting
      if (typeof valueA === "string") valueA = valueA.toLowerCase();
      if (typeof valueB === "string") valueB = valueB.toLowerCase();

      const comparison = valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      return sortType === "asc" ? comparison : -comparison;
    });
  }, [data, sortColumn, sortType]);

  // Sort column handler
  const handleSortColumn = (
    columnKey: string,
    nextSortType: "asc" | "desc"
  ) => {
    setSortColumn(columnKey);
    setSortType(nextSortType);
  };

  const triggerExport = () => {
    if (onExport) {
      onExport();
      setIsExportTriggered(true);
    }
  };

  useEffect(() => {
    if (isExportTriggered && csvLinkRef.current && exportData.length > 0) {
      csvLinkRef.current.link.click();
      setIsExportTriggered(false);
    }
  }, [exportData, isExportTriggered]);

const handleInputChange = async (rowId: number, columnKey: string, newValue: any) => {
    const updatedData = [...data];
    const rowToUpdate = updatedData.find((row) => row.id === rowId);

    if (!rowToUpdate) {
      console.log("rowToUpdate", rowToUpdate);
      return;
    }

    // Allow direct input changes, including deletion
    rowToUpdate[columnKey] = newValue;

    // For related fields, use the original value if input is empty
    if (columnKey === "FinalForecastOverride") {
      let originalForecast = 0;
      if (rowToUpdate.FinalForecastOverride) {
        if (rowToUpdate.FinalForecastOverride >= 0) {
          originalForecast = rowToUpdate.FinalForecastOverride;
        }
      } else if (rowToUpdate.dropdownBasedForecast.UserForecast) {
        originalForecast = rowToUpdate.dropdownBasedForecast.UserForecast;
      } else {
        originalForecast = rowToUpdate.TotalForecast
      }
      rowToUpdate["FinalAdjustedForecast"] = newValue || Math.floor(originalForecast);
    }

    if (columnKey === "SOQOverride") {
      if (!rowToUpdate._originalFinalSOQ) {
        rowToUpdate._originalFinalSOQ = rowToUpdate.FinalSOQ;
      }

      rowToUpdate.SOQOverride = newValue;

      if (newValue === "" || newValue === null || newValue === undefined) {
        rowToUpdate.FinalSOQ = rowToUpdate._originalFinalSOQ;
      } else {
        rowToUpdate.FinalSOQ = parseInt(newValue) || Math.floor(rowToUpdate._originalFinalSOQ);
      }
    }

    setTimeout(() => {
      setModifiedRows(prevSet => {
        // Convert previous Set to array and filter out the row being updated
        const filteredRows = Array.from(prevSet).filter((row: any) => row.id !== rowId);
        
        // Add the updated row
        const newRows = [...filteredRows, rowToUpdate];
        
        // Send update with all modified rows
        onDataChange(updatedData, newRows);
        
        // Return new Set with all modifications
        return new Set(newRows);
      });
    }, 0);
    
};

  const handleDropdownChange = (
    rowId: number,
    columnKey: string,
    value: string
  ) => {
    const updatedData = [...data];
    const rowToUpdate = updatedData.find((row) => row.id === rowId);

    if (!rowToUpdate) {
      console.error(`Row with ID ${rowId} not found.`);
      return;
    }

    // Update the dropdown value for the column key
    rowToUpdate[columnKey] = value;

    // Track modified rows
    setModifiedRows((prevSet) => {
      const prevArray = Array.from(prevSet);

      // Remove any previous version of this row
      const filteredRows = prevArray.filter((row: any) => row.id !== rowId);

      // Add the updated version
      filteredRows.push(rowToUpdate);

      // Create new Set with unique rows
      return new Set(filteredRows);
    });

    // Immediately evaluate the condition on the updated row
    if (
      rowToUpdate["ScenarioUnitPrice"] &&
      rowToUpdate["AdTypeScenario"] &&
      rowToUpdate["ScenarioUnitPrice"] !== "Select" &&
      rowToUpdate["AdTypeScenario"] !== "Select"
    ) {
      onDropdownAction(rowId, rowToUpdate);
    } else {
      onDropdownAction(rowId, rowToUpdate);
      console.log(
        "Condition not met: Missing Scenario Unit Price or Ad Type Scenario values"
      );
    }
  };


  const handleRowClick = (rowData: any) => {
    if (!rowData) {
      console.log("No data for clicked row.");
      return;
    }
    onRowSelect && onRowSelect([], rowData);

    // Use Id and Item for the unique key
    const newKey = `${rowData.id}_${rowData.Item}`;

    // Update the selectedRowKey, toggle if already selected
    const nextKey = selectedRowKey === newKey ? null : newKey;

    setSelectedRowKey(nextKey);
  };

  return (
    <div className="relative">
      <Table
        height={tableHeight}
        loading={loading}
        data={sortedData} // Display sorted and paginated data
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn as any}
        className="table-container"
        headerHeight={87}
        rowHeight={50}
        onRowClick={handleRowClick}
        rowClassName={(rowData, rowIndex) => {
          if (!rowData) return "";
          const key = `${rowData.id}_${rowData.Item}`;
          let className = "rs-table-row"; // Default class for all rows

          // Alternate row background for even rows
          if (rowIndex % 2 === 0) {
            className += " db-tablebg";
          }

          // Highlight only the selected row
          if (key === selectedRowKey) {
            className += " rs-row-selection";
          }

          return className;
        }}
      >
        {columns.map((column) => (
          <Column
            key={column.key}
            width={column.width}
            align={column.align || "left"}
            fixed={column.fixed}
            sortable={!!column.sortable}
            flexGrow={1}
            minWidth={column.width}
          >
            {/* Render Dynamic HeaderCell */}
           <HeaderCell>
            <Whisper 
                followCursor 
                trigger="hover" 
                speaker={
                    <Tooltip>
                        <div dangerouslySetInnerHTML={{ __html: column.title }} />
                    </Tooltip>
                }
                disabled={!column.title || column.title.length <= 12}
            >
                <div
                    dangerouslySetInnerHTML={{
                        __html: column.title,
                    }}
                    style={{
                        lineHeight: "1.5",
                    }}
                    className="table-header-title font-semibold truncate"
                />
            </Whisper>
        </HeaderCell>

            {/* Render Cell Content */}
            <Cell dataKey={column.key}>
              {(rowData, rowIndex) => {
                const cellValue = rowData[column.key];
                // Dropdown Cell
                if (column.cellType === "dropdown") {
                  return (
                    <div className="relative w-full table-cell-title font-normal">
                      <SelectPicker
                        size={"xs"}
                        data={column.options || []}
                         value={cellValue}
                         valueKey="value"
                         labelKey="label" 
                        searchable={false}
                        className="w-full"
                        container={() => document.body}
                        menuStyle={{
                          position: 'fixed',
                          maxHeight: '200px',
                          overflow: 'auto',
                          width: 'auto',
                        }}
                        placement="autoVerticalStart"
                        onChange={(value) =>
                          handleDropdownChange(
                            rowData.id as number,
                            column.key,
                            value
                          )
                        }
                      />
                    </div>
                  );
                }

                if (column.cellType === "input") {
                  return (
                    <Input
                      className="pl-4 pr-4 w-full table-cell-title font-normal"
                      onKeyPress={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      value={cellValue || ""}
                      min={0}
                      style={{
                        paddingLeft: '10px',
                        paddingRight: '10px'
                      }}
                      onChange={(value) => {
                        if (rowData.id !== undefined) {
                          handleInputChange(rowData.id, column.key, value);
                        } else {
                          console.error(
                            `Row ID is undefined for rowData:`,
                            rowData
                          );
                        }
                      }}
                    />
                  );
                }
                // Default Cell
               return (
                <Whisper 
                    followCursor 
                    trigger="hover" 
                    speaker={<Tooltip>{cellValue}</Tooltip>}
                    disabled={!cellValue || cellValue.length < 20} 
                >
                    <span className="table-cell-title font-normal truncate">
                        {cellValue}
                    </span>
                </Whisper>
            );
              }}
            </Cell>
          </Column>
        ))}
      </Table>

      {/* Centered Pagination and Go-To Page */}
      <div className=" flex justify-center items-center mt-4 mb-0 mx-4 relative flex-1 min-h-0 flex-col">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          total={totalRows} // Total rows in the dataset
          limit={limit} // Rows per page
          limitOptions={[10, 30, 50, 100]} // Dropdown options for rows per page
          activePage={currentPage} // Current active page
          onChangePage={(page) => {
            setActivePage(page); // Update active page state
            onPageChange(page); // Notify parent of page change
          }}
          onChangeLimit={(newLimit) => {
            setLimit(newLimit); // Change rows per page dynamically
            setActivePage(1); // Reset to the first page when rows per page changes
          }}
          layout={["pager", "skip"]}
          maxButtons={3} // LIMIT visible buttons to 7 for cleaner pagination
          size="md"
        />

        <div className="absolute right-0 flex gap-2">
          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={modifiedRows.size === 0}
            className={`px-[20px] h-[30px] text-white font-semibold rounded ${modifiedRows.size === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#949494] hover:bg-gray-600"
              } btn-text`}
          >
            Save
          </Button>

          {/* Export Button */}
          <Button
            onClick={triggerExport}
            className="px-[20px] h-[30px] btn-text text-white font-semibold rounded bg-[#86BC25] hover:bg-green-600 "
          >
            Export
          </Button>

          {/* Hidden CSVLink */}
          <CSVLink
            data={exportData}
            headers={exportHeaders}
            filename="ExportData.csv"
            ref={csvLinkRef}
            className="hidden"
          />

          {/* Update Button */}
          <button
            onClick={handleUpdate}
            disabled={!isUpdateEnabled}
            className={`px-[20px] h-[30px]  text-white font-semibold rounded ${isUpdateEnabled
              ? "bg-[#62B5E5] hover:bg-blue-600" // Update Button color when enabled
              : "bg-gray-400 cursor-not-allowed" // Disabled state
              } btn-text `}
          >
            Update SOQ
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableComponent;
